import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/pt-br';
import tz from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

import { ClientKind, QuestionType } from '../api/enumerations';
import {
  Icon123MS,
  IconCalendarMonthMS,
  IconCheckBoxMS,
  IconCheckCircleMS,
  IconNotesMS,
  IconRadioButtonCheckedMS,
} from '../constants/icons';

export const cleanValue = (value: string): string => {
  const cleaned = value.replace(/\D/g, '');
  return cleaned;
};

export const checkCardColor = (
  type: number,
  over?: boolean,
  alert?: boolean,
  calculation?: boolean
): string => {
  if (over || calculation) {
    return '#820B0B';
  }
  if (alert) {
    return '#EBA839';
  }
  if (type === ClientKind.BRASIL_BANK) {
    return '#F5F430';
  }
  if (type === ClientKind.CAIXA) {
    return '#005CA9 ';
  }
  if (type === ClientKind.CORPORATE) {
    return '#1A1A1A';
  }
  return '#004D43';
};

export const checkQuestionSelection = (type: number): boolean => {
  if (type === QuestionType.MULTIPLE_CHOICE) {
    return true;
  }
  return false;
};

export const checkQuestionIcon = (type: number): JSX.Element => {
  if (type === QuestionType.SHORT_ANSWER) {
    return IconNotesMS;
  }
  if (type === QuestionType.SINGLE_SELECTION) {
    return IconRadioButtonCheckedMS;
  }
  if (type === QuestionType.MULTIPLE_CHOICE) {
    return IconCheckBoxMS;
  }
  if (type === QuestionType.LIST) {
    return IconCheckCircleMS;
  }
  if (type === QuestionType.DATE) {
    return IconCalendarMonthMS;
  }
  if (type === QuestionType.NUMERIC) {
    return Icon123MS;
  }
  return IconNotesMS;
};

export const convertDate = (date: string): string => {
  const formatDate = new Date(date);
  const localDate = formatDate.toLocaleDateString('pt-br');
  return `${localDate}`;
};

export const convertPercentage = (value: number): string => {
  const percent = value * 100;
  const decimal = percent.toFixed(2);
  const parts = decimal.toString().split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return parts.join(',');
};

export const convertToFloatNumber = (value: string): number => {
  const newValue = value.replace(/[^\d,]/g, '');
  const valueToNumber = parseFloat(newValue.replace(',', '.'));
  return valueToNumber;
};

export const convertFloatDot = (value: string): number => {
  const temp = value.replace(' %', '');
  const newValue = temp.replace(/(\d{2})(\d{2})/, '$1.$2');
  return Number(newValue);
};

export const formatCep = (value: string): string => {
  const parts = cleanValue(value);
  const newValue = parts.replace(/(\d{5})(\d)/, '$1-$2');
  return newValue;
};

export const formatCPF = (cpf: string): string => {
  const cleaned = cpf.replace(/\D/g, '');

  const formatted = cleaned.replace(
    /(\d{3})(\d{3})(\d{3})(\d{2})/,
    '$1.$2.$3-$4'
  );

  return formatted;
};

export const formatCnpjCpf = (value: string | null | undefined): string => {
  if (!value) return '—';
  const cnpjCpf = value.replace(/\D/g, '');

  if (cnpjCpf.length < 12) {
    return cnpjCpf
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
  }

  return cnpjCpf
    .replace(/(\d{2})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1/$2')
    .replace(/(\d{4})(\d{1,2})$/, '$1-$2');
};

export const formatDate = (date: string | undefined): string => {
  if (!date) return '—';
  const parts = date.split('-');
  if (parts.length !== 3) return '—';
  return parts.reverse().join('/');
};

export const formatOnlyDate = (date: string | undefined): string => {
  if (date) {
    dayjs.extend(utc);
    dayjs.extend(tz);
    const dateTime = dayjs(date).tz('America/Sao_Paulo');
    const formattedHour = dateTime.format('DD/MM/YYYY');

    return formattedHour;
  }
  return '';
};

export const formatDateMonthName = (
  date: string | null | undefined
): string => {
  if (!date) return '—';
  dayjs.extend(utc);
  dayjs.extend(tz);
  return dayjs(date)
    .tz('America/Sao_Paulo')
    .locale('pt-br')
    .format('DD MMM [de] YYYY');
};

export const formatDateLongMonth = (
  date: string | null | undefined
): string => {
  if (!date) return '—';
  dayjs.extend(utc);
  dayjs.extend(tz);
  return dayjs(date)
    .tz('America/Sao_Paulo')
    .locale('pt-br')
    .format('DD [de] MMMM [de] YYYY');
};

export const formatDateLongMonthLocal = (
  date: string | null | undefined
): string => {
  if (!date) return '—';
  dayjs.extend(utc);
  dayjs.extend(tz);
  return dayjs(date).locale('pt-br').format('DD [de] MMMM [de] YYYY');
};

export const formatDateAndTime = (date: string | undefined): string => {
  if (date) {
    dayjs.extend(utc);
    dayjs.extend(tz);
    const dateTime = dayjs(date).tz('America/Sao_Paulo');
    const formattedHour = dateTime.format('DD/MM/YYYY - HH:mm');

    return formattedHour;
  }
  return '';
};

// Formata um datetime que já vem em horário de São Paulo, sem sufixo de
// timezone. Interpreta como UTC e formata sem `.tz()` para preservar os
// componentes literais — não converter deslocaria o horário conforme o fuso
// do navegador.
export const formatLocalDateTime = (
  date: string | null | undefined
): string => {
  if (!date) return '—';
  dayjs.extend(utc);
  return dayjs.utc(date).format('DD/MM/YYYY [·] HH:mm');
};

export const formatSaoPauloDateTime = (
  date: string | null | undefined
): string => {
  if (!date) return '—';
  dayjs.extend(utc);
  dayjs.extend(tz);
  return dayjs.utc(date).tz('America/Sao_Paulo').format('DD/MM/YYYY [·] HH:mm');
};

export const formatFloatNumber = (value: number): string => {
  const parts = value.toString().split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return parts.join(',');
};

export const formatFirstLetter = (value: string): string => {
  const newValue = value.charAt(0).toUpperCase();
  return newValue + value.slice(1);
};

export const normalizeText = (value: string): string =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

export const formatFloatNumberDecimal = (value: number): string => {
  const decimal = value.toFixed(2);
  const parts = decimal.toString().split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return parts.join(',');
};

export const formatFloatNumberDot = (value: number): string => {
  const decimal = value.toFixed(2);
  const parts = decimal.toString().split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return parts.join(',');
};

export const formatPhone = (phoneNumber: string): string => {
  const cleaned = cleanValue(phoneNumber);
  const digit = cleaned.replace(/(\d{2})(\d)/, '($1) $2');
  const newValue = digit.replace(/(\d)(\d{4})$/, '$1-$2');
  return newValue;
};

export const formatPhoneNumber = (
  phoneNumber: string | null | undefined
): string => {
  if (!phoneNumber) return '—';
  const cleaned = cleanValue(phoneNumber).replace(/^55(\d{11})$/, '$1');
  const match = cleaned.match(/^(\d{2})(\d{5})(\d{4})$/);
  if (match) {
    return `(${match[1]}) ${match[2]}-${match[3]}`;
  }
  return phoneNumber;
};

export const formatDecimalPlaces = (value: number): number => {
  if (value % 1 !== 0) {
    return Number(value.toFixed(3));
  }
  return value;
};

export const formatReal = (input: number): string => {
  if (!input) return '';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(input);
};

export const fmtCurrency = (value: number | null | undefined): string => {
  if (value == null) return '-';
  return `R$ ${value.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

export const fmtPercent = (value: number | null | undefined): string => {
  if (value == null) return '-';
  return `${value} %`;
};

export const formatTime = (date: Dayjs): string => {
  const roundDate = date.second(0);
  const formattedTime = roundDate.format('HH:mm:ss');
  return formattedTime;
};

export const getMissingDays = (today: Dayjs, until: Dayjs): string => {
  if (today.isSame(until, 'd')) {
    return '0 dias';
  }
  const missingDays = until.diff(today, 'd') + 1;
  if (missingDays === 1) {
    return `${missingDays} dia`;
  }
  return `${missingDays} dias`;
};

export const generateRandomHexColor = (): string => {
  const letters = '0123456789ABCDEF';
  return `#${Array.from(
    { length: 6 },
    () => letters[Math.floor(Math.random() * 16)]
  ).join('')}`;
};

export const getRandomNumber = (min: number, max: number): number => {
  return Math.floor(Math.random() * (max - min)) + min;
};

interface IOption {
  id: number;
  isUtilized: boolean;
  position: google.maps.LatLngLiteral;
}

export const offsetDuplicateCoord = (coordinates: IOption[]): IOption[] => {
  const seenCoordinates: { [key: string]: boolean } = {};
  const R = 6378137.0;

  // the duplicate coord will be set to the North and East of its origin using the Haversine formula
  return coordinates.map((option) => {
    const coord = option.position;
    const key = `${coord.lat.toFixed(6)},${coord.lng.toFixed(6)}`;

    const DistanceNorth = getRandomNumber(3, 7);
    const DistanceEast = getRandomNumber(3, 7);

    const updatedOption = { ...option, position: { ...coord } };

    if (seenCoordinates[key]) {
      const dLat = DistanceNorth / R;
      const dLon = DistanceEast / (R * Math.cos((Math.PI * coord.lat) / 180));

      updatedOption.position.lat += (dLat * 180) / Math.PI;
      updatedOption.position.lng += (dLon * 180) / Math.PI;
    } else {
      seenCoordinates[key] = true;
    }

    return updatedOption;
  });
};

export const validateCellphone = (cellphone: string): boolean => {
  const cellphoneValue = cleanValue(cellphone);

  return /[1-9]{2}9[1-9]{1}[0-9]{7}/g.test(cellphoneValue);
};

export const validatePhone = (phone: string): boolean => {
  const phoneValue = cleanValue(phone);

  return /[1-9]{2}[0-9]{7}/g.test(phoneValue);
};

export const validateEmail = (email: string): boolean => {
  return /\S+@\S+\.\S+/.test(email);
};

export const validateImg = (e: string): boolean => {
  return /^image/.test(e);
};

export const validateFiles = (e: string): boolean => {
  return /^image\/(png|jpe?g)$|^application\/(pdf)/.test(e);
};

export const validatePassword = (password: string): boolean => {
  // Requires 8 characters, at least one uppercase, one lowercase and one number
  return /^(?!.* )(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[a-zA-Z\d\w\W]{8,}$/.test(
    password
  );
};

export const validateUserName = (name: string): boolean => {
  return /^[^0-9]+$/.test(name);
};

export const getCompanySubdomain = (): string => {
  // Local dev / tunneled access: force the configured tenant so the API host
  // does not get derived from the browser hostname (e.g. a tunnel domain).
  if (process.env.REACT_APP_DEFAULT_SUBDOMAIN) {
    return process.env.REACT_APP_DEFAULT_SUBDOMAIN;
  }
  const url = window.location.href;
  const match = url.match(/\/\/(.*?)\.(com\.br|localhost)/);
  const companySubdomain = match ? match[1] : 'realprice-st.protoai';

  return companySubdomain;
};

export const matchCompanySubdomain = (): string => {
  // Same override as above, but returns the first label (the value stored in
  // the JWT's company.subdomain), so post-login routing matches the tenant.
  if (process.env.REACT_APP_DEFAULT_SUBDOMAIN) {
    return process.env.REACT_APP_DEFAULT_SUBDOMAIN.split('.')[0];
  }
  const url = window.location.href;
  const match = url.match(/\/\/([^.]+)/);
  const companySubdomain = match ? match[1] : 'realprice-st';

  return companySubdomain;
};

export const isProtoBank = (): boolean => {
  const currentsubdomain = getCompanySubdomain();
  return currentsubdomain.includes('bancoproto-st');
};

export const isPlaceholderSelectOption = (
  value: number | string | boolean
): boolean => value === false || value === 0 || value === '' || value == null;

export const booleanToText = (valor: boolean): string => {
  return valor ? 'verdadeiro' : 'falso';
};

export const convertToFloatNumberWithNegative = (value: string): number => {
  const newValue = value.replace(/[^\d,-]/g, '');
  const valueToNumber = parseFloat(newValue.replace(',', '.'));
  return valueToNumber;
};

export const formatMillisToMinutesSeconds = (millis: number): string => {
  const totalSeconds = Math.floor(millis / 1000);

  if (totalSeconds > 0 && totalSeconds < 60) {
    return '1 minuto';
  }

  const totalMinutes = Math.floor(totalSeconds / 60);

  if (totalMinutes < 60) {
    return `${totalMinutes} ${totalMinutes === 1 ? 'minuto' : 'minutos'}`;
  }

  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  const hourLabel = hours === 1 ? 'hora' : 'horas';
  const minutesLabel = minutes === 1 ? 'minuto' : 'minutos';

  if (minutes === 0) {
    return `${hours} ${hourLabel}`;
  }

  return `${hours} ${hourLabel} e ${minutes} ${minutesLabel}`;
};
